'use client'

import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard,
  Link as LinkIcon,
  QrCode,
  Settings,
  Plus,
  MousePointerClick,
  Copy,
  ExternalLink,
  BarChart2,
  Search,
  MoreVertical,
  Globe,
  ShoppingBag,
  Gift,
  ClipboardList,
  Users,
  Film
} from 'lucide-react'
import { useEffect, useState } from 'react'

export const SECTIONS = [
  {
    id: 'overview',
    title: 'Overview',
    subtitle: 'Your platform at a glance',
    btnText: 'View All',
    icon: LayoutDashboard,
    items: [
      { id: 1, title: 'Total Links', url: 'Active & Tracked', clicks: 124 },
      { id: 2, title: 'Total Clicks', url: 'Last 30 days', clicks: 45200 },
      { id: 3, title: 'Active QR Codes', url: 'High engagement', clicks: 56 },
    ]
  },
  {
    id: 'links',
    title: 'Links',
    subtitle: 'Manage your shortened URLs',
    btnText: 'Create Link',
    icon: LinkIcon,
    items: [
      { id: 1, title: 'Summer Campaign', url: 'lenklyst.com/summer24', clicks: 1240 },
      { id: 2, title: 'Product Launch', url: 'lenklyst.com/launch-v2', clicks: 856 },
      { id: 3, title: 'Social Bio', url: 'lenklyst.com/my-profile', clicks: 3120 },
    ]
  },
  {
    id: 'qr',
    title: 'QR Codes',
    subtitle: 'Track dynamic codes',
    btnText: 'Create QR',
    icon: QrCode,
    items: [
      { id: 1, title: 'Store Front', url: 'lenklyst.com/qr/store', clicks: 450 },
      { id: 2, title: 'Event RSVP', url: 'lenklyst.com/qr/event', clicks: 890 },
      { id: 3, title: 'Menu Card', url: 'lenklyst.com/qr/menu', clicks: 120 },
    ]
  },
  {
    id: 'bio',
    title: 'Link in Bio & Reels',
    subtitle: 'Manage bio page & reel codes',
    btnText: 'Create Link',
    icon: Globe,
    items: [
      { id: 1, title: 'Creator Hub #24', url: 'lenklyst.com/bio/you?code=24', clicks: 5600 },
      { id: 2, title: 'Video Preset #LUT', url: 'lenklyst.com/bio/you?code=LUT', clicks: 3200 },
      { id: 3, title: 'Notion Template #KIT', url: 'lenklyst.com/bio/you?code=KIT', clicks: 1850 },
    ]
  },
  {
    id: 'products',
    title: 'Products',
    subtitle: 'Manage your store items',
    btnText: 'Add Product',
    icon: ShoppingBag,
    items: [
      { id: 1, title: 'Digital Course', url: '$49.00', clicks: 124 },
      { id: 2, title: 'E-book PDF', url: '$12.00', clicks: 560 },
      { id: 3, title: 'Consultation', url: '$150.00', clicks: 42 },
    ]
  },
  {
    id: 'referrals',
    title: 'Referrals',
    subtitle: 'Track referral growth',
    btnText: 'New Campaign',
    icon: Gift,
    items: [
      { id: 1, title: 'Ambassador Prg', url: '8 Active', clicks: 1240 },
      { id: 2, title: 'Early Access', url: '15 Active', clicks: 856 },
    ]
  },
  {
    id: 'forms',
    title: 'Forms & Polls',
    subtitle: 'Collect user insights',
    btnText: 'Create Form',
    icon: ClipboardList,
    items: [
      { id: 1, title: 'User Feedback', url: '45 entries', clicks: 124 },
      { id: 2, title: 'Waitlist', url: '1.2k entries', clicks: 1200 },
      { id: 3, title: 'Product Poll', url: '85 votes', clicks: 85 },
    ]
  },
  {
    id: 'team',
    title: 'Team',
    subtitle: 'Manage collaborators',
    btnText: 'Invite Member',
    icon: Users,
    items: [
      { id: 1, title: 'Piyush (Admin)', url: 'Owner', clicks: 100 },
      { id: 2, title: 'Sarah Smith', url: 'Editor', clicks: 88 },
      { id: 3, title: 'Mike John', url: 'Viewer', clicks: 76 },
    ]
  },
  {
    id: 'analytics',
    title: 'Analytics',
    subtitle: 'Track performance',
    btnText: 'Export CSV',
    icon: BarChart2,
    items: [
      { id: 1, title: 'Total Clicks', url: 'All time', clicks: 45200 },
      { id: 2, title: 'Avg Conversion', url: 'This month', clicks: 18 },
    ]
  }
]

export function DashboardPreview({ sectionId, className = "" }: { sectionId: string, className?: string }) {
  const section = SECTIONS.find(s => s.id === sectionId) || SECTIONS[0]
  const sectionIndex = SECTIONS.findIndex(s => s.id === sectionId)

  return (
    <motion.div 
      initial={{ perspective: 1000, rotateX: 8, rotateY: 12, scale: 0.98, opacity: 0 }}
      whileInView={{ rotateX: 6, rotateY: 10, scale: 1, opacity: 1 }}
      viewport={{ once: true }}
      style={{ willChange: 'transform' }}
      className={`relative w-full aspect-[1.6/1] bg-[#050505] rounded-xl overflow-hidden shadow-2xl flex border border-white/5 ${className}`}
    >
      {/* Mini Sidebar */}
      <div className="w-10 md:w-12 border-r border-white/5 bg-white/[0.01] flex flex-col items-center py-3 gap-2.5 z-20 shrink-0">
        <div className="mb-1.5 flex flex-col items-center">
          <span className="text-md font-bold tracking-tighter text-white">
            LL<span className="text-primary">.</span>
          </span>
        </div>

        {SECTIONS.filter((_, i) => i < 7).map((s, i) => (
           <div 
             key={s.id}
             className={`p-1.5 rounded-lg transition-colors ${sectionIndex === i ? 'bg-primary/20 text-primary' : 'bg-white/[0.02] text-white/5'}`}
           >
             <s.icon className="w-3 h-3" />
           </div>
        ))}
      </div>

      {/* Content Area */}
      <div className="flex-1 p-3 md:p-4 flex flex-col gap-3 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] sm:text-xs font-bold text-white tracking-tight">{section.title}</p>
            <p className="text-[7px] text-white/20 mt-0.5">{section.subtitle}</p>
          </div>
          <div className="px-2 py-1 bg-primary text-white rounded-md flex items-center gap-1 shadow-[0_0_10px_rgba(0,102,255,0.2)]">
            <Plus className="w-2 h-2" />
            <span className="text-[5px] font-bold uppercase tracking-wider">{section.btnText}</span>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-6 bg-white/[0.02] border border-white/5 rounded-md px-2 flex items-center gap-1.5">
            <Search className="w-2.5 h-2.5 text-white/10" />
            <span className="text-[7px] text-white/10">Search...</span>
          </div>
        </div>

        {/* List of Items */}
        <div className="flex flex-col gap-1.5">
          {section.items.slice(0, 3).map((item, idx) => (
            <div
              key={item.id}
              className="p-2 rounded-lg border border-white/5 bg-white/[0.01] flex items-center justify-between"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="w-7 h-5 rounded-md bg-primary/10 flex items-center justify-center text-primary border border-primary/10">
                  <section.icon className="w-3 h-3" />
                </div>
                <div className="min-w-0">
                  <div className="text-[8px] font-bold text-white/80 truncate mb-0.5">{item.title}</div>
                  <div className="text-primary/40 font-mono text-[6px] truncate leading-none">
                    {item.url}
                  </div>
                </div>
              </div>

              <div className="text-right">
                 <p className="text-[8px] font-bold text-white tabular-nums tracking-tight">
                  {item.clicks.toLocaleString()}
                </p>
                <p className="text-[6px] text-white/20 uppercase font-black">
                   {section.id === 'analytics' || section.id === 'overview' ? 'Value' : section.id === 'products' ? 'Sold' : 'Clicks'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Ambient glows */}
      <div className="absolute top-0 right-0 w-[150px] h-[150px] bg-primary/5 rounded-full blur-[40px] pointer-events-none z-10" />
      <div className="absolute bottom-0 left-0 w-[120px] h-[120px] bg-primary/5 rounded-full blur-[30px] pointer-events-none z-10" />
    </motion.div>
  )
}

export function DashboardAnimation() {
  const [sectionIndex, setSectionIndex] = useState(0)

  // Rotate sections automatically
  useEffect(() => {
    const interval = setInterval(() => {
      setSectionIndex(prev => (prev + 1) % SECTIONS.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [])

  const currentSection = SECTIONS[sectionIndex]

  return (
    <div className="relative w-full aspect-[1.6/1] group perspective-[2000px]">
      {/* Background Layers for Stacked Effect */}
      <div 
        className="absolute inset-0 rounded-3xl border border-white/5 bg-primary/5 backdrop-blur-sm -z-20 translate-y-12 translate-x-12 scale-[0.88] opacity-30 shadow-2xl transition-all duration-700 group-hover:translate-y-14 group-hover:translate-x-14 group-hover:scale-[0.86] group-hover:opacity-20"
      />
      <div 
        className="absolute inset-0 rounded-3xl border border-white/10 bg-[#050505]/60 backdrop-blur-md -z-10 translate-y-6 translate-x-6 scale-[0.94] opacity-70 shadow-2xl transition-all duration-700 group-hover:translate-y-8 group-hover:translate-x-8 group-hover:scale-[0.92] group-hover:opacity-50"
      />

      <motion.div 
        initial={{ rotateX: 8, rotateY: 12, scale: 0.98 }}
        animate={{ rotateX: 6, rotateY: 10 }}
        transition={{ duration: 5, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
        style={{ willChange: 'transform' }}
        className="relative w-full h-full rounded-3xl border border-white/20 bg-[#050505]/95 backdrop-blur-2xl overflow-hidden shadow-2xl flex"
      >
      {/* Mini Sidebar */}
      <div className="w-12 md:w-14 border-r border-white/5 bg-white/[0.01] flex flex-col items-center py-4 gap-3 z-20 shrink-0">
        <div className="mb-2 flex flex-col items-center">
          <span className="text-lg font-bold tracking-tighter text-white">
            LL<span className="text-primary">.</span>
          </span>
        </div>

        {SECTIONS.filter((_, i) => i < 8).map((section, i) => (
           <div 
             key={section.id}
             className={`p-1.5 rounded-lg transition-colors ${sectionIndex === i ? 'bg-primary/20 text-primary' : 'bg-white/[0.02] text-white/10'}`}
           >
             <section.icon className="w-3.5 h-3.5" />
           </div>
        ))}

        <div className="mt-auto mb-2">
          <div className="p-1.5 rounded-lg bg-white/[0.02] text-white/10">
            <Settings className="w-3.5 h-3.5" />
          </div>
        </div>
      </div>

      {/* Cyclic Content Area */}
      <div className="flex-1 relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSection.id}
            initial={{ opacity: 0, y: 15, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -15, scale: 0.98 }}
            transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
            className="absolute inset-0 p-4 md:p-6 flex flex-col gap-4"
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-white tracking-tight">{currentSection.title}</p>
                <p className="text-[9px] text-white/25 mt-0.5">{currentSection.subtitle}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="px-2.5 py-1.5 bg-primary text-white rounded-lg flex items-center gap-1.5 shadow-[0_0_15px_rgba(0,102,255,0.2)]">
                  <Plus className="w-2.5 h-2.5" />
                  <span className="text-[6px] font-bold uppercase tracking-wider">{currentSection.btnText}</span>
                </div>
              </div>
            </div>

            {/* Filter Bar */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-7 bg-white/[0.02] border border-white/5 rounded-lg px-3 flex items-center gap-2">
                <Search className="w-3 h-3 text-white/20" />
                <span className="text-[8px] text-white/20">Search...</span>
              </div>
              <div className="flex items-center gap-2 text-[7px] text-white/20 bg-white/[0.02] border border-white/5 rounded-lg px-2 py-1">
                <div className="w-1 h-1 rounded-full bg-emerald-500" />
                <span>Healthy</span>
              </div>
            </div>

            {/* List of Items */}
            <div className="flex flex-col gap-2">
              {currentSection.items.slice(0, 3).map((item, idx) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -5 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + idx * 0.05 }}
                  className="p-3 rounded-xl border border-white/5 bg-white/[0.01] flex items-center justify-between hover:bg-white/[0.03] transition-all duration-300"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-9 h-6 rounded-md bg-primary/10 flex items-center justify-center text-primary border border-primary/10">
                      <currentSection.icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <div className="text-[10px] font-bold text-white/90 truncate">{item.title}</div>
                        <div className="w-1 h-1 rounded-full bg-emerald-500/50" />
                      </div>
                      <div className="text-primary/40 font-mono text-[7px] truncate leading-none">
                        {item.url}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-5">
                    <div className="text-right">
                       <p className="text-[10px] font-bold text-white tabular-nums tracking-tight">
                        {item.clicks.toLocaleString()}
                      </p>
                      <p className="text-[7px] text-white/20 uppercase font-black">
                        {currentSection.id === 'analytics' || currentSection.id === 'overview' ? 'Value' : currentSection.id === 'products' ? 'Sold' : 'Clicks'}
                      </p>
                    </div>
                    <div className="hidden sm:block p-1 rounded-md bg-white/5 text-white/30">
                      <Copy className="w-3 h-3" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Decorative ambient glows */}
      <div className="absolute top-0 right-0 w-[250px] h-[250px] bg-primary/10 rounded-full blur-[80px] pointer-events-none z-10" />
      <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-primary/5 rounded-full blur-[60px] pointer-events-none z-10" />
      </motion.div>
    </div>
  )
}
